/**
 * Design system component: CTAButton
 *
 * Full-width primary action button with appearance driven by `variant`:
 *   - `request`   → accent orange bg + colour-matched shadow (Request to Borrow)
 *   - `return`    → success green bg + colour-matched shadow (Mark as Returned)
 *   - `danger`    → danger red bg + colour-matched shadow (Delete, Sign Out)
 *   - `secondary` → white bg + border, no shadow (outline/secondary actions)
 *   - `disabled`  → muted bg + border, not-allowed cursor, click blocked
 *   - `owner`     → non-interactive label panel (shown to item owners in place of the borrow button)
 *   - `info`      → soft blue bg + border, not-allowed cursor (return pending state)
 *
 * Colour-matched button shadows (`SHADOW.btn.*`) reinforce semantic meaning
 * and add visual depth without generic grey shadows.
 */
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { ReactNode } from 'react';

type CTAVariant = 'request' | 'return' | 'owner' | 'disabled' | 'danger' | 'secondary' | 'info';

interface CTAButtonProps {
  variant: CTAVariant;
  onClick?: () => void;
  children: ReactNode;
}

/**
 *
 * @param root0
 * @param root0.variant
 * @param root0.onClick
 * @param root0.children
 */
export function CTAButton({ variant, onClick, children }: CTAButtonProps) {
  if (variant === 'owner') {
    return (
      <div
        style={{
          padding: '12px 16px',
          background: T.surfaceSubtle,
          borderRadius: RADIUS.md,
          border: `1px solid ${T.border}`,
          textAlign: 'center',
        }}
      >
        <span style={{ fontSize: 13, color: T.textMuted }}>{children}</span>
      </div>
    );
  }

  const styles: Record<Exclude<CTAVariant, 'owner'>, React.CSSProperties> = {
    request: {
      background: T.accent,
      color: T.white,
      border: 'none',
      cursor: 'pointer',
      boxShadow: SHADOW.btn.accent,
    },
    return: {
      background: T.success,
      color: T.white,
      border: 'none',
      cursor: 'pointer',
      boxShadow: SHADOW.btn.success,
    },
    danger: {
      background: T.danger,
      color: T.white,
      border: 'none',
      cursor: 'pointer',
      boxShadow: SHADOW.btn.danger,
    },
    secondary: {
      background: T.surface,
      color: T.textSec,
      border: `1px solid ${T.border}`,
      cursor: 'pointer',
    },
    disabled: {
      background: T.surfaceSubtle,
      color: T.textMuted,
      border: `1px solid ${T.border}`,
      cursor: 'not-allowed',
    },
    info: {
      background: T.warningSoft,
      color: T.warning,
      border: `1px solid ${T.warningBorder}`,
      cursor: 'not-allowed',
    },
  };

  return (
    <button
      onClick={variant !== 'disabled' && variant !== 'info' ? onClick : undefined}
      disabled={variant === 'disabled' || variant === 'info'}
      style={{
        width: '100%',
        padding: '14px 0',
        borderRadius: RADIUS.md,
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        fontFamily: 'inherit',
        ...styles[variant as Exclude<CTAVariant, 'owner'>],
      }}
    >
      {children}
    </button>
  );
}
